import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';

import { Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';
import KiloActionIcon from 'components/KiloActionIcon';
import KiloButton from 'components/KiloButton';
import KiloInput from 'components/KiloInput';
import KiloTooltip, { TOOLTIP_MESSAGES } from 'components/KiloTooltip';
import useCreateRevenueCategory from 'hooks/revenueCategory/useCreateRevenueCategory';
import useDeleteRevenueCategory from 'hooks/revenueCategory/useDeleteRevenueCategory';
import useUpdateRevenueCategory from 'hooks/revenueCategory/useUpdateRevenueCategory';
import { RevenueCategory } from 'types';
import { nameValidator } from 'utils/form-validators';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface IRevenueCategoryFormProps {
  category?: RevenueCategory;
  isNew?: boolean;
  onClose?: () => void;
  permission?: boolean;
  setCreateCategoryIsOpen: Dispatch<SetStateAction<boolean>>;
}

const RevenueCategoryForm = ({
  category,
  isNew,
  onClose,
  setCreateCategoryIsOpen,
  permission = true,
}: IRevenueCategoryFormProps): JSX.Element => {
  const createMutation = useCreateRevenueCategory();
  const updateMutation = useUpdateRevenueCategory();
  const deleteMutation = useDeleteRevenueCategory();
  const { successNotification, errorNotification } = notificationHandler();

  const initialCategory = useMemo<RevenueCategory>(() => {
    if (category) return category;
    return {
      name: '',
      inUse: false,
      revenueCategoryId: uuidv4(),
      createdAt: '',
      gym: '',
      updatedAt: '',
    };
  }, [category]);

  const creationMode = isNew ?? !category;

  const form = useForm<RevenueCategory>({
    initialValues: initialCategory,
    validate: {
      name: value => {
        if (!value || value.length > 120) return 'Name must be between 1-120 characters';
        return nameValidator(value);
      },
    },
  });

  useEffect(() => {
    form.setValues(initialCategory);
    form.resetDirty(initialCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategory.revenueCategoryId]);

  const handleSave = (): void => {
    if (form.validate().hasErrors) return;

    const values = form.values;

    const onSuccess = (saved: RevenueCategory): void => {
      form.setValues(saved);
      form.resetDirty(saved);
    };

    if (creationMode) {
      createMutation.mutate(values, {
        onSuccess: response => {
          successNotification('Category created', `${response.data.name} successfully created.`);
          onSuccess(response.data);
          setCreateCategoryIsOpen(false);
        },
        onError: error => {
          errorNotification('Category creation failed', error.response?.data.message);
        },
      });
    } else {
      updateMutation.mutate(values, {
        onSuccess: response => {
          successNotification('Category updated', `${response.data.name} successfully updated.`);
          onSuccess(response.data);
        },
        onError: error => {
          errorNotification('Category update failed', error.response?.data.message);
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (creationMode) {
      onClose!();
      return;
    }

    deleteMutation.mutate(initialCategory.revenueCategoryId, {
      onSuccess: () => {
        successNotification('Category deleted');
      },
      onError: error => {
        errorNotification('Category deletion failed', error.response?.data.message);
      },
    });
  };

  return (
    <Group align='flex-end'>
      <KiloInput
        label='Category name'
        disabled={!permission}
        required
        {...form.getInputProps('name')}
        data-cy='categoryNameTextInput'
        variantStyle='outlinedWithLabel'
        withAsterisk={false}
      />
      <Group noWrap spacing='xs'>
        <KiloButton
          rootVariantType='revenueCategories'
          type='button'
          onClick={handleSave}
          disabled={!permission || !form.isDirty('name')}
        >
          Save
        </KiloButton>

        <KiloTooltip
          label={TOOLTIP_MESSAGES.DELETE_CATEGORY_RESTRICTED}
          position='right'
          disabled={!initialCategory.inUse}
        >
          <KiloActionIcon disabled={!!initialCategory.inUse} onClick={handleDelete}>
            <IconTrash size='1.2rem' />
          </KiloActionIcon>
        </KiloTooltip>
      </Group>
    </Group>
  );
};

export default RevenueCategoryForm;
